import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { fetchEpisodes, type MemoryEpisode } from "@/api/memory";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type CRMEpisode = MemoryEpisode & {
  type?: string;
  agents?: string[];
  graph_node_id?: string;
};

type EntityType = "lead" | "event" | "venue";

const ENTITY_PARAM: Record<EntityType, keyof ReturnType<typeof buildFilter>> = {
  lead: "lead_id",
  event: "event_id",
  venue: "venue_id",
};

const entityLabels: Record<EntityType, string> = {
  lead: "Lead",
  event: "Event",
  venue: "Venue",
};

function buildFilter() {
  return {
    lead_id: "",
    event_id: "",
    venue_id: "",
  };
}

function formatDate(timestamp?: string) {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleString();
}

type CRMEpisodesListProps = {
  entityType: EntityType;
  entityId?: string | number | null;
  limit?: number;
  condensed?: boolean;
  className?: string;
  emptyMessage?: string;
};

export function CRMEpisodesList({
  entityType,
  entityId,
  limit = 10,
  condensed = false,
  className,
  emptyMessage,
}: CRMEpisodesListProps) {
  const [episodes, setEpisodes] = useState<CRMEpisode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const filter = buildFilter();
    const key = ENTITY_PARAM[entityType];
    filter[key] = String(entityId);
    fetchEpisodes({ ...filter, limit })
      .then((data) => {
        if (cancelled) return;
        setEpisodes(data as CRMEpisode[]);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Unable to load episodes.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [entityId, entityType, limit]);

  const displayedEpisodes = useMemo(() => {
    if (!condensed) return episodes;
    return episodes.slice(0, 3);
  }, [condensed, episodes]);

  if (!entityId) {
    return (
      <div className={className}>
        <p className="text-sm text-muted-foreground">Link this {entityLabels[entityType].toLowerCase()} to see episode history.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {loading && !episodes.length ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : null}
      {error && <p className="text-xs text-rose-400">{error}</p>}
      {!loading && !episodes.length && !error && (
        <p className="text-sm text-muted-foreground">{emptyMessage ?? "No memory episodes linked yet."}</p>
      )}
      <div className="space-y-3">
        {displayedEpisodes.map((episode) => (
          <article
            key={episode.id}
            className="rounded-lg border border-border bg-background/60 p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{episode.type ?? "Episode"}</p>
                <p className="text-sm font-semibold text-foreground">{episode.summary}</p>
              </div>
              <Badge variant="outline">{formatDate(episode.timestamp)}</Badge>
            </div>
            {episode.agents?.length ? (
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {episode.agents.map((agent) => (
                  <span
                    key={`${episode.id}-${agent}`}
                    className="rounded-full bg-background/40 px-2 py-0.5 text-[11px] uppercase tracking-wide text-primary-foreground dark:text-amber-200"
                  >
                    {agent}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
              <Link
                to={`/memory?tab=graph&node=${encodeURIComponent(episode.graph_node_id ?? episode.id)}`}
                className="text-amber-300 hover:text-primary-foreground dark:text-amber-100"
              >
                Open memory graph
              </Link>
              <Link
                to={`/memory?tab=episodes&${ENTITY_PARAM[entityType]}=${encodeURIComponent(String(entityId))}`}
                className="text-muted-foreground hover:text-foreground/80"
              >
                View episode detail
              </Link>
            </div>
          </article>
        ))}
      </div>
      {condensed && episodes.length > displayedEpisodes.length ? (
        <div className="mt-3">
          <Button asChild variant="outline" className="w-full border-dashed border-border text-xs uppercase tracking-wide">
            <Link to={`/memory?tab=episodes&${ENTITY_PARAM[entityType]}=${encodeURIComponent(String(entityId))}`}>
              See all episodes
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
